import React from 'react';
import { MapPin, Users, Video, Clock, User } from 'lucide-react';

const STATUS_DOT = {
  ACCEPTED: 'bg-emerald-500',
  DECLINED: 'bg-red-500',
  TENTATIVE: 'bg-amber-500',
};

function formatRange(start, end) {
  if (!start) return '';
  const d = (v) => v?.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const t = (v) => v?.toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (!end) return d(start);
  const sameDay = start.toDateString() === end.toDateString();
  return sameDay ? `${d(start)} — ${t(end)}` : `${d(start)} — ${d(end)}`;
}

export default function EventHoverCard({ event, position }) {
  if (!event) return null;

  const { extendedProps } = event;
  const meta = extendedProps?.colaboradorColor;
  const attendees = Array.isArray(extendedProps?.attendees) ? extendedProps.attendees : [];

  const CARD_WIDTH = 340;
  const CARD_HEIGHT_ESTIMATE = 300;
  const PADDING = 8;
  const left = Math.max(PADDING, Math.min(position.x, window.innerWidth - CARD_WIDTH - PADDING));
  const top = Math.max(PADDING, Math.min(position.y + 10, window.innerHeight - CARD_HEIGHT_ESTIMATE - PADDING));

  const style = { position: 'fixed', left, top, zIndex: 9999, width: CARD_WIDTH };

  return (
    <div
      style={style}
      className="bg-white rounded-xl shadow-2xl border border-slate-200 p-4 space-y-3 pointer-events-none"
    >
      <div className="flex items-start gap-2">
        {meta && (
          <span
            className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold leading-none"
            style={{ backgroundColor: meta.bg, color: meta.text }}
          >
            {meta.initial}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm text-slate-900 leading-tight break-words">{event.title}</h3>
          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <Clock size={11} className="flex-shrink-0" />
            <span>{formatRange(event.start, event.end)}</span>
          </p>
        </div>
      </div>

      {extendedProps?.location && (
        <div className="flex items-start gap-2 text-xs text-slate-600">
          <MapPin size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
          <span className="break-words line-clamp-2">{extendedProps.location}</span>
        </div>
      )}

      {attendees.length > 0 && (
        <div className="flex items-start gap-2 text-xs">
          <Users size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="space-y-0.5 min-w-0">
            {attendees.slice(0, 5).map((att, i) => (
              <div key={`${att?.email || i}-${i}`} className="flex items-center gap-1.5 text-slate-600">
                <span
                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    STATUS_DOT[(att?.status || '').toUpperCase()] || 'bg-slate-300'
                  }`}
                />
                <span className="truncate">{att?.name || att?.email || '—'}</span>
              </div>
            ))}
            {attendees.length > 5 && (
              <span className="text-slate-400">+{attendees.length - 5} mais</span>
            )}
          </div>
        </div>
      )}

      {extendedProps?.meetUrl && (
        <div className="flex items-center gap-2 text-xs text-emerald-700">
          <Video size={13} /> Google Meet disponível
        </div>
      )}

      {extendedProps?.organizer && (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <User size={11} /> Organizado por <span className="text-slate-600">{extendedProps.organizer}</span>
        </div>
      )}

      {extendedProps?.isExternal && (
        <div className="text-[10px] text-slate-400 border-t border-slate-100 pt-1.5">
          🌐 Calendário externo{meta?.name ? ` · ${meta.name}` : ''}
        </div>
      )}
    </div>
  );
}
